import { z } from 'zod';
/**
 * Timezone a tenant's exams are scheduled and displayed in, used whenever a
 * school document carries no (or an unusable) `meta.timeZone`.
 */
export const DEFAULT_SCHOOL_TIME_ZONE = 'Asia/Jakarta';
/**
 * Whether a value is an IANA zone this runtime can format with. A malformed
 * zone makes `Intl.DateTimeFormat` throw a RangeError, which inside a Vue
 * template blanks the page — so every zone that reaches a formatter is checked
 * here first and falls back to the default instead.
 *
 * Results are memoized because constructing a formatter costs ~460µs while
 * reusing one costs ~6µs, and the picker validates a zone per card on every
 * clock tick. The answer is a pure function of the zone string and the
 * process's ICU data, so it cannot go stale within a process.
 */
const supportedTimeZones = new Map();
export function isSupportedTimeZone(zone) {
    if (typeof zone !== 'string' || !zone.trim())
        return false;
    const cached = supportedTimeZones.get(zone);
    if (cached !== undefined)
        return cached;
    let supported;
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: zone });
        supported = true;
    }
    catch {
        supported = false;
    }
    supportedTimeZones.set(zone, supported);
    return supported;
}
/**
 * Per-tenant settings stored under the `settings` field of the
 * `schools/{schoolId}` Firestore document.
 *
 * The school doc itself is written ad hoc (sync, bootstrap scripts); this
 * schema only formalizes the `settings` map, so parse `doc.settings ?? {}`,
 * never the whole document.
 */
export const SchoolSettings = z.object({
    // When on, students can call quiz-taking session endpoints only from the
    // CBT Super App, identified by its User-Agent prefix. Server-side deterrent
    // against casual browser access — the UA is client-controlled, so this is
    // not hard security. Never affects admin/teacher/proctor roles.
    requireSuperAppForStudents: z.boolean().default(false),
    // Which structure the academic relationship readers trust (AIMSIS-18812).
    // `legacy` keeps every authorization path on user.classroomIds/subjectIds —
    // the pre-migration behaviour, and the default, so a tenant that never runs
    // the migration is unaffected and only an admin's explicit switch moves the
    // source of truth. `classMemberships` makes Class Memberships authoritative
    // for student classroom relationships. The legacy arrays are still written
    // and still readable either way, so the switch stays reversible.
    academicRelationshipSource: z.enum(['legacy', 'classMemberships']).default('legacy'),
});
export const ProvisionSchoolRequest = z.object({
    schoolId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
    schoolName: z.string().min(1).max(200),
    aimsisSchoolId: z.number().int().positive().optional(),
    timeZone: z.string().default(DEFAULT_SCHOOL_TIME_ZONE),
    plan: z.string().default('mvp'),
    admin: z.object({
        email: z.string().email(),
        password: z.string().min(8).max(200),
        displayName: z.string().min(1).max(200),
    }),
    settings: SchoolSettings.partial().optional(),
});
export const ProvisionSchoolResponse = z.object({
    schoolId: z.string(),
    admin: z.object({
        uid: z.string(),
        email: z.string().email(),
    }),
    created: z.boolean(),
});
export const SchoolSlugCheckRequest = z
    .object({
    schoolName: z.string().min(1).max(200).optional(),
    slug: z.string().min(1).max(63).optional(),
})
    .refine(value => value.schoolName !== undefined || value.slug !== undefined);
export const ProvisionIngressRequest = z.object({
    schoolId: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/),
});
export const ProvisionGcipTenantRequest = z.object({
    schoolId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
    displayName: z.string().regex(/^[A-Za-z][A-Za-z0-9-]{3,19}$/).optional(),
    emailPassword: z.boolean().optional(),
    totpMfa: z.boolean().optional(),
});
export const ProvisionGcipTenantResponse = z.object({
    schoolId: z.string(),
    authTenantId: z.string(),
    created: z.boolean(),
});
export const ProvisionIngressResponse = z.object({
    schoolId: z.string(),
    fqdn: z.string(),
    status: z.enum(['active', 'pending']),
    created: z.boolean(),
    dnsRecords: z.array(z.object({
        type: z.enum(['A', 'AAAA', 'TXT', 'CNAME']),
        name: z.string(),
        state: z.enum(['present', 'created', 'conflict']),
    })),
    dnsConflicts: z.array(z.object({ type: z.enum(['A', 'AAAA', 'TXT', 'CNAME']), name: z.string() })),
});
export const UpdateSchoolSettingsRequest = z.object({
    requireSuperAppForStudents: z.boolean().optional(),
    academicRelationshipSource: z.enum(['legacy', 'classMemberships']).optional(),
});
//# sourceMappingURL=school.js.map